import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import Modal from '../../components/ui/Modal';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconCheck,
  IconX,
  IconTractor,
  IconClock,
  IconCurrencyPound,
  IconAlertTriangle,
  IconLock,
} from '@tabler/icons-react';

export default function JobDetail() {
  const { language } = useTranslation();
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile, role } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !jobId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId, 'jobs', jobId));
        if (snap.exists()) setJob({ id: snap.id, ...snap.data() });
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId, jobId]);

  const handleApproveEstimate = async () => {
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'jobs', jobId), {
        status: 'approved',
        estimateApprovedAt: serverTimestamp(),
        estimateApprovedBy: userProfile?.name,
        updatedAt: serverTimestamp(),
      });
      setJob(prev => ({ ...prev, status: 'approved', estimateApprovedBy: userProfile?.name }));
      toast.success('Estimate approved — dealer notified');
    } catch (err) { toast.error('Failed to approve estimate'); }
    setUpdating(false);
  };

  const handleDeclineEstimate = async () => {
    if (!declineReason.trim()) return;
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'jobs', jobId), {
        status: 'declined',
        estimateDeclinedAt: serverTimestamp(),
        estimateDeclinedBy: userProfile?.name,
        declineReason: declineReason.trim(),
        updatedAt: serverTimestamp(),
      });
      setJob(prev => ({ ...prev, status: 'declined', declineReason: declineReason.trim() }));
      setShowDeclineModal(false);
      toast.success('Estimate declined');
    } catch (err) { toast.error('Failed to decline estimate'); }
    setUpdating(false);
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const urgencyColour = (u) => {
    if (u === 'critical') return 'var(--color-danger)';
    if (u === 'high') return 'var(--color-warning)';
    if (u === 'medium') return 'var(--color-brand)';
    return 'var(--color-text-tertiary)';
  };

  if (loading) return <Layout><div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div></Layout>;

  if (!job) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/jobs')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Job requests
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Job not found</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>

        <div onClick={() => navigate('/jobs')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Job requests
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ fontSize: 20, fontWeight: 400 }}>Job request</h1>
              <StatusPill status={job.status || 'pending'} size="lg" />
              {job.urgency && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: urgencyColour(job.urgency) + '18', color: urgencyColour(job.urgency), textTransform: 'capitalize' }}>
                  {job.urgency} urgency
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              Raised by {job.createdByName} · {formatDateTime(job.createdAt)}
            </div>
          </div>
        </div>

        {job.urgency === 'critical' && job.status !== 'completed' && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <IconAlertTriangle size={16} color="var(--color-danger)" stroke={1.5} />
            <div style={{ fontSize: 13, color: 'var(--color-danger)' }}>
              Critical urgency — machine should not be used until this job is completed
            </div>
          </div>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Job details</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '0.5px solid var(--color-border-light)', marginBottom: 4 }}>
            <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconTractor size={18} color="#185FA5" stroke={1.5} />
            </div>
            <div
              onClick={() => job.machineId && navigate('/machines/' + job.machineId)}
              style={{ cursor: job.machineId ? 'pointer' : 'default' }}
            >
              <div style={{ fontSize: 14, color: job.machineId ? 'var(--color-brand)' : 'var(--color-text-primary)' }}>
                {job.machineName || '—'}
              </div>
              {job.machineReg && (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, background: '#FAC775', color: '#412402', padding: '1px 5px', borderRadius: 2 }}>
                  {job.machineReg}
                </span>
              )}
            </div>
          </div>
          <div style={{ padding: '12px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>Description</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6 }}>{job.description}</div>
          </div>
          {job.notes && (
            <div style={{ padding: '10px 0' }}>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>Additional notes</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6 }}>{job.notes}</div>
            </div>
          )}
        </Card>

        {job.estimate && (
          <Card style={{ marginBottom: 16, border: job.status === 'estimate-sent' ? '0.5px solid var(--color-brand)' : '0.5px solid var(--color-border-light)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 400 }}>Estimate from dealer</div>
              {job.status === 'estimate-sent' && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'var(--color-brand-light)', color: 'var(--color-brand)' }}>
                  Awaiting approval
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <IconCurrencyPound size={20} color="var(--color-text-primary)" stroke={1.5} />
              <div style={{ fontSize: 28, fontWeight: 400, color: 'var(--color-text-primary)' }}>
                {job.estimate.amount ? '£' + Number(job.estimate.amount).toLocaleString() : '—'}
              </div>
            </div>
            {job.estimate.description && (
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: 14 }}>
                {job.estimate.description}
              </div>
            )}
            {job.status === 'estimate-sent' && (role === 'org_admin' || role === 'manager') && (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="danger-outline"
                  icon={<IconX size={14} stroke={1.5} />}
                  onClick={() => setShowDeclineModal(true)}
                  disabled={updating}
                >
                  Decline estimate
                </Button>
                <Button
                  variant="success"
                  fullWidth
                  icon={<IconCheck size={14} stroke={1.5} />}
                  onClick={handleApproveEstimate}
                  disabled={updating}
                  loading={updating}
                >
                  Approve estimate
                </Button>
              </div>
            )}
            {job.status === 'approved' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-success)' }}>
                <IconCheck size={15} color="var(--color-success)" stroke={2} />
                Approved by {job.estimateApprovedBy} · {formatDateTime(job.estimateApprovedAt)}
              </div>
            )}
            {job.status === 'declined' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-danger)', marginBottom: 6 }}>
                  <IconX size={15} color="var(--color-danger)" stroke={2} />
                  Declined by {job.estimateDeclinedBy}
                </div>
                {job.declineReason && (
                  <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{job.declineReason}</div>
                )}
              </div>
            )}
          </Card>
        )}

        {job.status === 'completed' && job.jobCardId && (
          <Card style={{ border: '0.5px solid var(--color-success)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-success-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconLock size={18} color="var(--color-success)" stroke={1.5} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-success)', fontWeight: 400 }}>
                  Job completed — sealed record on machine profile
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                  {formatDateTime(job.completedAt)}
                </div>
              </div>
            </div>
            {job.machineId && (
              <Button
                variant="secondary"
                size="sm"
                style={{ marginTop: 12 }}
                onClick={() => navigate('/machines/' + job.machineId)}
              >
                View machine profile
              </Button>
            )}
          </Card>
        )}

      </div>

      <Modal
        open={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        title="Decline estimate"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowDeclineModal(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={handleDeclineEstimate}
              disabled={updating || !declineReason.trim()}
              loading={updating}
              icon={<IconX size={14} stroke={1.5} />}
            >
              Decline estimate
            </Button>
          </>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
            Reason for declining <span style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <textarea
            placeholder="e.g. Too expensive — will seek alternative quote"
            value={declineReason}
            onChange={e => setDeclineReason(e.target.value)}
            rows={3}
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            autoFocus
          />
        </div>
      </Modal>

    </Layout>
  );
}